import React, { useMemo } from "react";

import { Controller } from "react-hook-form";

import { CodeInput } from "src/base-components/CodeInput/CodeInput";
import { EditorBoundsProvider } from "src/base-components/CodeInput/useEditorBounds";
import {
  DateRange,
  DateRangePicker,
} from "src/base-components/DateRangePicker";
import { getDefaultDateRange } from "src/base-components/FilterPopover/constants";
import { OperatorSelect } from "src/base-components/FilterPopover/OperatorSelect";
import {
  ConditionRenderContext,
  OperatorOption,
} from "src/base-components/FilterPopover/types";
import { OperatorDropdown } from "src/base-components/OperatorDropdown";
import { Pill } from "src/base-components/Pill";
import { Option, Select } from "src/base-components/Select";

const IS_NONE_OPERATOR = "is_none";

type OperatorDropdownInputProps = {
  groupIndex: number;
  conditionIndex: number;
  operatorOptions: OperatorOption[];
  codeInputValue?: string;
  codeInputReadOnly?: boolean;
  onValueChange: (value: string) => void;
  placeholder?: string;
  error?: string | boolean;
  disabled?: boolean;
};

export const OperatorDropdownInput: React.FC<OperatorDropdownInputProps> = ({
  groupIndex,
  conditionIndex,
  operatorOptions,
  codeInputValue = "",
  codeInputReadOnly = false,
  onValueChange,
  placeholder,
  error,
  disabled = false,
}) => {
  const operatorPath =
    `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.operator` as const;
  const dataLoc = `group-${groupIndex}-condition-${conditionIndex}-operator`;

  return (
    <EditorBoundsProvider maxX={100}>
      <CodeInput
        dataLoc={`group-${groupIndex}-condition-${conditionIndex}-value`}
        disabled={disabled}
        error={error}
        placeholder={placeholder}
        readOnly={codeInputReadOnly}
        value={codeInputValue}
        onChange={onValueChange}
      >
        {disabled ? (
          <OperatorDropdown
            dataLoc={dataLoc}
            disabled
            options={operatorOptions}
            value={operatorOptions[0]?.key ?? ""}
            onChange={() => {}}
          />
        ) : (
          <Controller
            name={operatorPath}
            render={({ field: operatorField }) => (
              <OperatorDropdown
                dataLoc={dataLoc}
                options={operatorOptions}
                value={operatorField.value as string}
                onChange={operatorField.onChange}
              />
            )}
          />
        )}
      </CodeInput>
    </EditorBoundsProvider>
  );
};

const parseJsonArray = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseDateRange = (value: string | undefined): DateRange | undefined => {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    return {
      from: parsed.from ? new Date(parsed.from) : undefined,
      to: parsed.to ? new Date(parsed.to) : undefined,
    };
  } catch {
    return undefined;
  }
};

type WithOperatorDropdownProps = {
  groupIndex: number;
  conditionIndex: number;
  operator: string;
  operatorOptions: OperatorOption[];
  children: React.ReactNode;
};

const WithOperatorDropdown: React.FC<WithOperatorDropdownProps> = ({
  groupIndex,
  conditionIndex,
  operator,
  operatorOptions,
  children,
}) => {
  const isNoneOperator = operator === IS_NONE_OPERATOR;
  const showOperatorDropdown = operatorOptions.length > 1;

  return (
    <div className="flex items-center gap-x-2">
      {showOperatorDropdown && (
        <div className="w-28 self-start">
          <Controller
            name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.operator`}
            render={({ field }) => (
              <OperatorSelect
                dataLoc={`group-${groupIndex}-condition-${conditionIndex}-operator`}
                options={operatorOptions}
                value={field.value as string}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      )}
      {!isNoneOperator && <div className="flex-1">{children}</div>}
    </div>
  );
};

const StringValueRenderer: React.FC<ConditionRenderContext> = ({
  groupIndex,
  conditionIndex,
  operator,
  value,
  onChange,
  error,
  operatorOptions,
}) => (
  <OperatorDropdownInput
    codeInputReadOnly={operator === IS_NONE_OPERATOR}
    codeInputValue={value}
    conditionIndex={conditionIndex}
    error={error}
    groupIndex={groupIndex}
    operatorOptions={operatorOptions}
    placeholder="Enter value"
    onValueChange={onChange}
  />
);

const BooleanValueRenderer: React.FC<ConditionRenderContext> = ({
  groupIndex,
  conditionIndex,
  operator,
  value,
  onChange,
  operatorOptions,
}) => (
  <WithOperatorDropdown
    conditionIndex={conditionIndex}
    groupIndex={groupIndex}
    operator={operator}
    operatorOptions={operatorOptions}
  >
    <Select
      dataLoc={`group-${groupIndex}-condition-${conditionIndex}-enum`}
      options={[
        { key: "True", value: "True" },
        { key: "False", value: "False" },
      ]}
      value={value}
      onChange={(v) => onChange(v as string)}
    />
  </WithOperatorDropdown>
);

const wrapOptionInPill = (option: Option): Option => {
  if (typeof option.value !== "string" && typeof option.value !== "number") {
    return option;
  }
  return {
    ...option,
    displayValue: option.displayValue || option.value,
    value: (
      <Pill size="sm" variant={option.pillVariant ?? "gray"}>
        {option.icon && <Pill.Icon icon={option.icon} />}
        <Pill.Text>{option.value}</Pill.Text>
      </Pill>
    ),
  };
};

const MultiSelectValueRenderer: React.FC<ConditionRenderContext> = ({
  groupIndex,
  conditionIndex,
  field,
  operator,
  value,
  onChange,
  operatorOptions,
}) => {
  const selectedValues = parseJsonArray(value);
  const pillOptions = useMemo(
    () => (field.options ?? []).map(wrapOptionInPill),
    [field.options],
  );

  return (
    <WithOperatorDropdown
      conditionIndex={conditionIndex}
      groupIndex={groupIndex}
      operator={operator}
      operatorOptions={operatorOptions}
    >
      <Select
        dataLoc={`group-${groupIndex}-condition-${conditionIndex}-${field.type}`}
        options={pillOptions}
        value={selectedValues}
        multiple
        onChange={(values) => {
          const next = values as string[];
          onChange(next.length > 0 ? JSON.stringify(next) : "");
        }}
      />
    </WithOperatorDropdown>
  );
};

const DateValueRenderer: React.FC<ConditionRenderContext> = ({
  groupIndex,
  conditionIndex,
  value,
  onChange,
}) => {
  const dateRange = useMemo(
    () => parseDateRange(value) ?? getDefaultDateRange(),
    [value],
  );

  const handleDateChange = (range?: DateRange) => {
    if (range) {
      onChange(
        JSON.stringify({
          from: range.from?.toISOString(),
          to: range.to?.toISOString(),
        }),
      );
    }
  };

  return (
    <DateRangePicker
      dataLoc={`group-${groupIndex}-condition-${conditionIndex}-datetime`}
      value={dateRange}
      usePredefinedDates
      onChange={handleDateChange}
    />
  );
};

export const BUILT_IN_RENDERERS: Record<
  string,
  React.FC<ConditionRenderContext>
> = {
  string: StringValueRenderer,
  number: StringValueRenderer,
  integer: StringValueRenderer,
  boolean: BooleanValueRenderer,
  enum: MultiSelectValueRenderer,
  tags: MultiSelectValueRenderer,
  date: DateValueRenderer,
  datetime: DateValueRenderer,
};
